import { endpoints } from 'core/api/endpoints';
import { apiDelete, apiGet, apiPatch, apiPost } from 'core/api/client';
import { serializeFilters } from 'shared/utilities/format';

export const CONSULTATION_STATUSES = [
  'pending_review',
  'matched',
  'scheduled',
  'completed',
  'cancelled',
  'rejected',
];

export const CONSULTATION_GOALS = [
  'understand_result',
  'choose_career_or_major',
  'change_career_path',
  'develop_current_path',
  'general_career_consultation',
];

export const CONSULTATION_PERIODS = [
  'morning',
  'afternoon',
  'evening',
  'flexible',
];

export const CONSULTATION_COMMUNICATION = [
  'video',
  'phone',
  'in_app',
];

export const SUPPORT_TICKET_STATUSES = [
  'open',
  'in_review',
  'resolved',
  'closed',
];

export const LEGAL_TYPES = ['terms', 'privacy'];
export const LEGAL_STATUSES = ['draft', 'published'];

function specialistListParams(filters = {}) {
  const params = {};
  if (filters.is_active === true) {
    params.is_active = 1;
  } else if (filters.is_active === false) {
    params.is_active = 0;
  }
  return params;
}

export function buildSpecialistFormData(fields, { avatarFile, removeAvatar } = {}) {
  const body = new FormData();
  const scalarKeys = [
    'name_ar',
    'name_en',
    'title_ar',
    'title_en',
    'bio_ar',
    'bio_en',
    'timezone',
  ];

  scalarKeys.forEach((key) => {
    const value = fields[key];
    if (value === undefined || value === null) {
      body.append(key, '');
      return;
    }
    body.append(key, String(value));
  });

  (fields.languages || []).forEach((code) => {
    body.append('languages[]', code);
  });

  body.append('is_active', fields.is_active ? '1' : '0');
  body.append('sort_order', String(fields.sort_order ?? 1));

  if (avatarFile) {
    body.append('avatar', avatarFile);
  }
  if (removeAvatar) {
    body.append('remove_avatar', '1');
  }

  return body;
}

export const operationsApi = {
  listSpecialists(filters, config) {
    const params = specialistListParams(filters || {});
    return apiGet(endpoints.specialists, {
      ...config,
      params: {
        ...(config?.params || {}),
        ...params,
      },
    });
  },
  showSpecialist(id, config) {
    return apiGet(endpoints.specialist(id), config);
  },
  createSpecialist(body, config) {
    return apiPost(endpoints.specialists, body, config);
  },
  updateSpecialist(id, body, config) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      body.append('_method', 'PATCH');
      return apiPost(endpoints.specialist(id), body, config);
    }
    return apiPatch(endpoints.specialist(id), body, config);
  },
  setSpecialistActivation(id, isActive, config) {
    return apiPatch(endpoints.specialistActivation(id), { is_active: isActive }, config);
  },
  deleteSpecialist(id, config) {
    return apiDelete(endpoints.specialist(id), config);
  },

  listConsultations(filters, config) {
    return apiGet(endpoints.consultations, { ...config, params: serializeFilters(filters) });
  },
  showConsultation(id, config) {
    return apiGet(endpoints.consultation(id), config);
  },
  consultationHistory(id, config) {
    return apiGet(endpoints.consultationHistory(id), config);
  },
  assignConsultation(id, specialistId, config) {
    return apiPost(endpoints.consultationAssign(id), { specialist_id: specialistId }, config);
  },
  scheduleConsultation(id, body, config) {
    return apiPost(endpoints.consultationSchedule(id), body, config);
  },
  completeConsultation(id, config) {
    return apiPost(endpoints.consultationComplete(id), {}, config);
  },
  rejectConsultation(id, reason, config) {
    return apiPost(endpoints.consultationReject(id), { reason }, config);
  },
  cancelConsultation(id, reason, config) {
    return apiPost(endpoints.consultationCancel(id), { reason }, config);
  },

  listSupportCategories(config) {
    return apiGet(endpoints.supportCategories, config);
  },
  createSupportCategory(body, config) {
    return apiPost(endpoints.supportCategories, body, config);
  },
  updateSupportCategory(id, body, config) {
    return apiPatch(endpoints.supportCategory(id), body, config);
  },
  deleteSupportCategory(id, config) {
    return apiDelete(endpoints.supportCategory(id), config);
  },
  listSupportTickets(filters, config) {
    return apiGet(endpoints.supportTickets, { ...config, params: serializeFilters(filters) });
  },
  showSupportTicket(id, config) {
    return apiGet(endpoints.supportTicket(id), config);
  },
  reviewSupportTicket(id, config) {
    return apiPost(endpoints.supportTicketReview(id), {}, config);
  },
  resolveSupportTicket(id, adminResponse, config) {
    return apiPost(endpoints.supportTicketResolve(id), { admin_response: adminResponse }, config);
  },
  closeSupportTicket(id, config) {
    return apiPost(endpoints.supportTicketClose(id), {}, config);
  },
  reopenSupportTicket(id, config) {
    return apiPost(endpoints.supportTicketReopen(id), {}, config);
  },

  listLegalDocuments(filters, config) {
    return apiGet(endpoints.legalDocuments, { ...config, params: serializeFilters(filters) });
  },
  showLegalDocument(id, config) {
    return apiGet(endpoints.legalDocument(id), config);
  },
  createLegalDocument(body, config) {
    return apiPost(endpoints.legalDocuments, body, config);
  },
  updateLegalDocument(id, body, config) {
    return apiPatch(endpoints.legalDocument(id), body, config);
  },
  publishLegalDocument(id, config) {
    return apiPost(endpoints.legalDocumentPublish(id), {}, config);
  },
};
